import { useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { Link } from 'react-router-dom';
import type { ComfortInput } from '@comforttool/comfortcore';
import {
  CHECKOUT_ADDONS,
  CHECKOUT_ADDON_IDS,
  formatPriceEur,
  geschatTotaalInclBtwMetAddons,
  FAIR_USE_MAX_KAMERS_STANDAARD,
  FAIR_USE_MAX_RAPPORT_REGENERATIES,
  type BundleTier,
  type CheckoutAddonId,
  type PrijsSnapshot,
} from '../lib/pricing';
import { LEGAL_CONTACT_EMAIL } from '../lib/legal-site-info';

type Props = {
  input: ComfortInput;
  prijsSnapshot: PrijsSnapshot;
  bundleTier: BundleTier;
  setBundleTier: Dispatch<SetStateAction<BundleTier>>;
  woonCheckoutOk: boolean;
  payEmail: string;
  setPayEmail: Dispatch<SetStateAction<string>>;
  checkoutLoading: boolean;
  checkoutError: string | null;
  onCheckout: () => void;
  indicativePricingParagraph: string;
  selectedAddons: CheckoutAddonId[];
  setSelectedAddons: Dispatch<SetStateAction<CheckoutAddonId[]>>;
};

export function ResultPurchaseSection({
  input,
  prijsSnapshot,
  bundleTier,
  setBundleTier,
  woonCheckoutOk,
  payEmail,
  setPayEmail,
  checkoutLoading,
  checkoutError,
  onCheckout,
  indicativePricingParagraph,
  selectedAddons,
  setSelectedAddons,
}: Props) {
  const [b2bOneTimeOpen, setB2bOneTimeOpen] = useState(false);

  const toggleAddon = (id: CheckoutAddonId) => {
    setSelectedAddons((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const addonLabelPrijs = (id: CheckoutAddonId) => {
    const a = CHECKOUT_ADDONS[id];
    return prijsSnapshot.requiresQuote
      ? ''
      : prijsSnapshot.isZakelijk
        ? `${formatPriceEur(a.eurExBtwB2B, 0)} ex btw`
        : `${formatPriceEur(a.eurInclBtwB2C)} incl. btw`;
  };

  const totaalHint = useMemo(() => {
    if (prijsSnapshot.requiresQuote) return null;
    return geschatTotaalInclBtwMetAddons(prijsSnapshot, selectedAddons);
  }, [prijsSnapshot, selectedAddons]);

  const checkoutPrijsHint = useMemo(() => {
    if (prijsSnapshot.requiresQuote) return 'Offerte — geen online betalen';
    if (totaalHint != null && (selectedAddons.length > 0 || !prijsSnapshot.isZakelijk)) {
      return `Naar betalen · totaal ca. ${formatPriceEur(totaalHint)} incl. btw`;
    }
    if (prijsSnapshot.isZakelijk) {
      return `Naar betalen · ca. ${formatPriceEur(prijsSnapshot.hoofdbedrag, 0)} ex btw`;
    }
    return `Naar betalen · ca. ${formatPriceEur(prijsSnapshot.hoofdbedrag)} incl. btw`;
  }, [prijsSnapshot, totaalHint, selectedAddons.length]);

  const tierChoice = (
    <div className="space-y-3 rounded-xl border border-ct-border/80 bg-ct-elevated/30 p-4">
      <p className="text-sm font-semibold text-ct-text">Kies je bundel</p>
      <label className="flex cursor-pointer gap-3 rounded-lg border border-ct-border bg-ct-surface px-3 py-2.5 has-[:checked]:border-ct-accent has-[:checked]:bg-ct-accent/10">
        <input
          type="radio"
          name="comfort-bundle-tier"
          checked={bundleTier === 'plus'}
          onChange={() => setBundleTier('plus')}
          className="mt-1"
        />
        <span>
          <span className="font-medium text-ct-text">Comfort Plus</span>
          <span className="mt-0.5 block text-sm text-ct-muted">
            Uitgebreid leesbaar adviesrapport — alsof iemand de cijfers voor je uitschrijft.
          </span>
        </span>
      </label>
      <label className="flex cursor-pointer gap-3 rounded-lg border border-ct-border bg-ct-surface px-3 py-2.5 has-[:checked]:border-ct-accent has-[:checked]:bg-ct-accent/10">
        <input
          type="radio"
          name="comfort-bundle-tier"
          checked={bundleTier === 'basis'}
          onChange={() => setBundleTier('basis')}
          className="mt-1"
        />
        <span>
          <span className="font-medium text-ct-text">Comfort Basis</span>
          <span className="mt-0.5 block text-sm text-ct-muted">
            Vaste, duidelijke samenvatting — vriendelijker geprijsd; dezelfde cijfers op je scherm.
          </span>
        </span>
      </label>
    </div>
  );

  if (prijsSnapshot.requiresQuote) {
    return (
      <div className="space-y-8">
        {woonCheckoutOk ? (
          <div className="rounded-2xl border border-ct-border bg-ct-surface p-6">
            <h3 className="font-bold text-ct-text">Offerte / maatwerk</h3>
            <p className="mt-2 text-sm leading-relaxed text-ct-muted">
              Voor jouw invoer ({prijsSnapshot.bandOmschrijving}) is er{' '}
              <strong className="text-ct-text">geen vaste webprijs</strong>. Online betalen gaat hier niet — we maken
              graag een offerte op maat.
            </p>
            <p className="mt-2 text-sm text-ct-muted">
              Vermeld het vloeroppervlak ({input.vloeroppervlakte} m²) en type gebouw in je bericht.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex rounded-xl bg-ct-accent px-6 py-3 text-sm font-bold text-white hover:bg-blue-500"
              >
                Naar contactformulier
              </Link>
              <a
                href={`mailto:${LEGAL_CONTACT_EMAIL}?subject=${encodeURIComponent('Offerte ComfortTool — groot pand')}`}
                className="inline-flex items-center rounded-xl border border-ct-border px-6 py-3 text-sm font-semibold text-ct-text hover:bg-ct-elevated"
              >
                Mail {LEGAL_CONTACT_EMAIL}
              </a>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  const showCheckoutForm = !prijsSnapshot.isZakelijk || b2bOneTimeOpen;

  return (
    <div className="space-y-8">
      <section className="rounded-2xl border border-ct-border bg-ct-surface p-6">
        <h3 className="font-bold text-ct-text">Product &amp; prijs</h3>
        {tierChoice}
        <p className="mt-4 text-sm text-ct-muted">
          {bundleTier === 'plus' ? (
            <>
              <strong className="text-ct-text">Comfort Plus:</strong> scan + scherm + printpagina +{' '}
              <strong className="text-ct-text">uitgebreid adviesrapport</strong> (als die optie bij jou aan staat).
            </>
          ) : (
            <>
              <strong className="text-ct-text">Comfort Basis:</strong> scan + scherm + printpagina +{' '}
              <strong className="text-ct-text">vaste tekstsamenvatting</strong> — zonder extra schrijf-laag.
            </>
          )}
        </p>
        <p className="mt-2 text-sm text-ct-muted">
          {prijsSnapshot.isZakelijk ? (
            <>
              Band <strong className="text-ct-text">{prijsSnapshot.band}</strong> —{' '}
              <strong className="text-ct-text">{formatPriceEur(prijsSnapshot.hoofdbedrag, 0)}</strong> ex btw, totaal{' '}
              <strong className="text-ct-text">{formatPriceEur(prijsSnapshot.totaalInclBtw)}</strong> incl. btw (
              {prijsSnapshot.bandOmschrijving}).
            </>
          ) : (
            <>
              Band <strong className="text-ct-text">{prijsSnapshot.band}</strong> —{' '}
              <strong className="text-ct-text">{formatPriceEur(prijsSnapshot.hoofdbedrag)}</strong> incl. btw (
              {prijsSnapshot.bandOmschrijving}).
            </>
          )}
        </p>
      </section>

      {prijsSnapshot.isZakelijk ? (
        <section className="rounded-2xl border border-ct-accent/30 bg-ct-elevated/50 p-6">
          <h3 className="font-bold text-ct-text">Organisatiebundel &amp; maatwerk</h3>
          <p className="mt-2 text-sm leading-relaxed text-ct-muted">
            Voor <strong className="text-ct-text">vaste aantallen rapporten per maand of kwartaal</strong>, staffel,
            factuur, <strong className="text-ct-text">white-label</strong> (logo/huisstijl) of koppelingen:{' '}
            <strong className="text-ct-text">offerte op maat</strong>. Bij Comfort Plus kun je optioneel een regel
            “namens …” op het Plus-rapport zetten; voor een volledige huisstijl-PDF werken we via contact.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="inline-flex rounded-xl bg-ct-accent px-6 py-3 text-sm font-bold text-white hover:bg-blue-500"
            >
              Vraag zakelijk voorstel
            </Link>
            <a
              href={`mailto:${LEGAL_CONTACT_EMAIL}?subject=${encodeURIComponent('ComfortTool — organisatiebundel / white-label')}`}
              className="inline-flex items-center rounded-xl border border-ct-border bg-ct-surface px-6 py-3 text-sm font-semibold text-ct-text hover:bg-ct-elevated"
            >
              Mail {LEGAL_CONTACT_EMAIL}
            </a>
          </div>
          <button
            type="button"
            onClick={() => setB2bOneTimeOpen((o) => !o)}
            className="mt-5 text-sm font-semibold text-ct-accent hover:underline"
            aria-expanded={b2bOneTimeOpen}
          >
            {b2bOneTimeOpen ? 'Verberg eenmalig online betalen' : 'Of: eenmalig dit rapport online betalen'}
          </button>
        </section>
      ) : null}

      <section className="rounded-2xl border border-ct-border bg-ct-surface p-6">
        <h3 className="font-bold text-ct-text">Comfort Pro — meer detail op deze pagina</h3>
        <p className="mt-2 text-sm text-ct-muted">
          <strong className="text-ct-text">Uur voor uur:</strong> gebruik de knop hoger op het resultaat om jaartotalen
          te laten berekenen op basis van weer uit het verleden.{' '}
          <strong className="text-ct-text">Scenario&apos;s:</strong> de tabel “Scenario-vergelijking” laat zien hoe het
          uitpakt bij een voorzichtig, midden of gunstig beeld — dezelfde rekenmethode als de kaarten erboven.
        </p>
      </section>

      {showCheckoutForm && woonCheckoutOk ? (
        <section className="rounded-2xl border border-ct-border bg-ct-surface p-6">
          <h3 className="font-bold text-ct-text">Optionele add-on</h3>
          <p className="mt-2 text-sm text-ct-muted">
            Eénmalig bij je bestelling. Een gratis tabel-overzicht kun je al downloaden op het resultaat (alleen wat je
            invulde en de uitkomsten op het scherm).
          </p>
          <ul className="mt-4 space-y-3">
            {CHECKOUT_ADDON_IDS.map((id) => {
              const a = CHECKOUT_ADDONS[id];
              const checked = selectedAddons.includes(id);
              return (
                <li key={id}>
                  <label className="flex cursor-pointer gap-3 rounded-xl border border-ct-border/80 bg-ct-elevated/30 px-4 py-3 transition hover:bg-ct-elevated/50">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleAddon(id)}
                      className="mt-1 h-4 w-4 rounded border-ct-border text-ct-accent"
                    />
                    <span>
                      <span className="font-semibold text-ct-text">{a.titel}</span>
                      <span className="ml-2 text-sm text-ct-accent">{addonLabelPrijs(id)}</span>
                      <span className="mt-1 block text-sm text-ct-muted">{a.beschrijving}</span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
          {totaalHint != null && selectedAddons.length > 0 ? (
            <p className="mt-4 text-sm font-medium text-ct-text">
              Geschat totaal incl. btw: {formatPriceEur(totaalHint)}
            </p>
          ) : null}
        </section>
      ) : null}

      {woonCheckoutOk && showCheckoutForm ? (
        <div className="rounded-2xl border border-ct-border bg-ct-surface p-6">
          <h3 className="font-bold text-ct-text">Afrekenen</h3>
          <p className="mt-2 text-sm text-ct-muted">
            Je betaalt via een bekende, beveiligde betaalpagina (Stripe). Bundel en eventuele extra’s staan op één bon.{' '}
            <Link to="/betaalmethoden" className="font-semibold text-ct-accent hover:underline">
              Welke betaalmethoden staan aan?
            </Link>
          </p>
          {prijsSnapshot.isZakelijk && (
            <ul className="mt-3 space-y-2 rounded-lg border border-ct-border/60 bg-ct-elevated/40 px-4 py-3 text-sm text-ct-muted">
              <li>
                Hoofdbundel (ex. btw):{' '}
                <strong className="text-ct-text">{formatPriceEur(prijsSnapshot.hoofdbedrag, 0)}</strong>
              </li>
              {selectedAddons.map((id) => (
                <li key={id}>
                  + {CHECKOUT_ADDONS[id].titel} (ex. btw):{' '}
                  <strong className="text-ct-text">{formatPriceEur(CHECKOUT_ADDONS[id].eurExBtwB2B, 0)}</strong>
                </li>
              ))}
              <li className="text-xs">Btw 21% staat op je betaalbewijs als aparte regels per onderdeel.</li>
              <li>
                Totaal indicatie incl. btw:{' '}
                <strong className="text-ct-text">
                  {formatPriceEur(geschatTotaalInclBtwMetAddons(prijsSnapshot, selectedAddons) ?? 0)}
                </strong>
              </li>
            </ul>
          )}
          <label className="mt-3 block text-sm text-ct-muted">E-mail voor de bon</label>
          <input
            type="email"
            value={payEmail}
            onChange={(e) => setPayEmail(e.target.value)}
            className="mt-1 w-full max-w-md rounded-lg border border-ct-border bg-ct-elevated px-3 py-2.5 text-ct-text"
            autoComplete="email"
          />
          {checkoutError && <p className="mt-2 text-sm text-red-400">{checkoutError}</p>}
          <button
            type="button"
            onClick={() => onCheckout()}
            disabled={checkoutLoading}
            className="mt-4 rounded-xl bg-white px-6 py-3 text-sm font-bold text-ct-bg hover:bg-ct-text disabled:opacity-50"
          >
            {checkoutLoading ? 'Bezig…' : checkoutPrijsHint}
          </button>
          <p className="mt-4 text-xs text-ct-muted">
            Fair use (beleid): max. {FAIR_USE_MAX_RAPPORT_REGENERATIES}× rapport opnieuw genereren per aankoop; max.{' '}
            {FAIR_USE_MAX_KAMERS_STANDAARD} kamers standaard — daarna contact.
          </p>
        </div>
      ) : null}

      {!woonCheckoutOk ? (
        <p className="text-center text-sm text-ct-muted">
          Online betalen staat nog niet aan voor deze omgeving — neem contact op als je tóch wilt afrekenen. (
          {indicativePricingParagraph})
        </p>
      ) : null}
    </div>
  );
}
